import { Lot, State } from '../types';
import { RemovalSelection } from './selection';

export type RemoveLotsFlagKey = 'removingClosedLots' | 'removingDeadMeat';

export interface RemovalDependencies {
  state: State;
  render: () => void;
  save: () => void;
  applyRemoteSnapshot: (lots: Lot[]) => void;
  ensureAccessToken: () => Promise<string | null>;
  DEBUG: boolean;
  deleteRemote: (
    id: string,
    favoriteOnly: boolean,
    state: State,
    ensureAccessToken: () => Promise<string | null>,
    DEBUG: boolean
  ) => Promise<unknown>;
}

export interface RemovalRequest extends RemovalDependencies {
  selection: RemovalSelection;
  runningFlagKey: RemoveLotsFlagKey;
}

export interface RemovalResult {
  removedCount: number;
  remoteErrors: { id: string; error: Error }[];
  error?: Error;
  skipped: boolean;
}

export function normalizeError(error: unknown): Error {
  if (error instanceof Error) {
    return error;
  }
  return new Error(String(error));
}

export async function runRemoval(request: RemovalRequest): Promise<RemovalResult> {
  const { selection, runningFlagKey, state } = request;
  if (state[runningFlagKey]) {
    return { removedCount: 0, remoteErrors: [], skipped: true };
  }
  if (!selection.targetLots.length) {
    return { removedCount: 0, remoteErrors: [], skipped: true };
  }

  state[runningFlagKey] = true;
  request.render();

  const remoteIdSet = new Set(selection.remoteIds);
  let removedCount = 0;
  const remoteErrors: { id: string; error: Error }[] = [];
  let fatalError: Error | undefined;

  try {
    if (selection.localIds.size) {
      state.lots = state.lots.filter(lot => !selection.localIds.has(lot.id));
      removedCount += selection.localIds.size;
      request.save();
      request.render();
    }

    if (selection.remoteIds.length) {
      if (!state.pendingFavoriteIds) {
        state.pendingFavoriteIds = new Set();
      }
      selection.remoteIds.forEach(id => {
        state.pendingFavoriteIds.add(id);
      });
      request.render();

      const results = await Promise.allSettled(
        selection.remoteIds.map(id =>
          request.deleteRemote(id, false, state, request.ensureAccessToken, request.DEBUG)
        )
      );

      const successfulRemoteIds = new Set<string>();

      results.forEach((result, index) => {
        const id = selection.remoteIds[index];
        if (result.status === 'fulfilled') {
          successfulRemoteIds.add(id);
        } else {
          remoteErrors.push({ id, error: normalizeError(result.reason) });
        }
      });

      selection.remoteIds.forEach(id => {
        state.pendingFavoriteIds.delete(id);
      });

      if (successfulRemoteIds.size) {
        state.lots = state.lots.filter(lot => !successfulRemoteIds.has(lot.id));
        removedCount += successfulRemoteIds.size;
        request.applyRemoteSnapshot(state.lots);
      }
    }
  } catch (error) {
    fatalError = normalizeError(error);
  } finally {
    if (state.pendingFavoriteIds && state.pendingFavoriteIds instanceof Set) {
      remoteIdSet.forEach(id => state.pendingFavoriteIds?.delete(id));
    }
    state[runningFlagKey] = false;
    request.render();
  }

  return { removedCount, remoteErrors, error: fatalError, skipped: false };
}

export interface RemoveSingleLotOptions {
  lot: Lot;
  state: State;
  render: () => void;
  applyRemoteSnapshot: (lots: Lot[]) => void;
  ensureAccessToken: () => Promise<string | null>;
  DEBUG: boolean;
  deleteRemote: RemovalDependencies['deleteRemote'];
}

export async function removeSingleLot(
  options: RemoveSingleLotOptions
): Promise<{ removed: boolean; error?: Error }> {
  const { lot, state } = options;

  if (!lot || !lot.id) {
    return { removed: false };
  }

  if (state.pendingFavoriteIds && state.pendingFavoriteIds.has(lot.id)) {
    return { removed: false };
  }

  if (!state.pendingFavoriteIds) {
    state.pendingFavoriteIds = new Set();
  }

  state.pendingFavoriteIds.add(lot.id);
  options.render();

  try {
    await options.deleteRemote(lot.id, false, state, options.ensureAccessToken, options.DEBUG);
    state.pendingFavoriteIds.delete(lot.id);
    state.lots = state.lots.filter(x => x.id !== lot.id);
    options.render();
    options.applyRemoteSnapshot(state.lots);
    return { removed: true };
  } catch (error) {
    state.pendingFavoriteIds.delete(lot.id);
    options.render();
    return { removed: false, error: normalizeError(error) };
  }
}
